"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.verify = verify;
const Persistent = require("./Persistent");
const Round_1 = require("./Round");
const Game_1 = require("./Game");
function verifyGame(ctx, game, rounds) {
    ctx.assertOk(game.completed, 'game must be completed');
    const roundPaths = Object.keys(rounds);
    ctx.assertGE(roundPaths.length, 2, 'Must have some rounds to verify');
    // Verify Options
    ctx.assertGT(game.options.maxPlayerCount, 0, 'maxPlayerCount must be greater than zero');
    ctx.assertLT(game.options.maxPlayerCount, 25, 'maxPlayerCount must be less than 25');
    ctx.assertGT(game.options.betUnit, 0, 'betUnit must be greater than zero');
    ctx.assertGT(game.options.betGrantCount, 0, 'betGrantCount must be greater than zero');
    ctx.assertGT(game.options.bestAnswerGrantCount, 0, 'betAnswerGrantCount must be greater than zero');
    ctx.assertGT(game.options.betSpotCount, 0, 'betSpotCount must be greater than zero');
    ctx.assertEqual((game.options.betSpotCount % 2), 1, `betSpotCount (${game.options.betSpotCount} must be odd`);
    ctx.assertGT(game.options.roundsPerGame, 0, 'roundsPerGame must be greater than zero');
    ctx.assertLT(game.options.roundsPerGame, 25, 'roundsPerGame must be less than 25');
    ctx.assertTrue(game.options.managerIsPlayer, 'managerIsPlayer must be true');
    ctx.assertOk(game.options.collapseDuplicateAnswers, 'Spread out duplicates are not supported by the verifier');
    // build up expectedGameBank as rounds are verified.
    const expectedGameBank = {};
    function assertBetNumber(ctx, value, range = 'non-negative') {
        ctx.assertEqual(typeof value, 'number', `Value ${value} expected to be a "number"`);
        if (range === 'non-negative') {
            ctx.assertGE(value, 0, `Value ${value} expected to be non-negative`);
        }
        ctx.assertEqual(value % game.options.betUnit, 0, `Value ${value} expected to be a multiple of $betUnit`);
    }
    function getExpectedGameBank(playerPath) {
        let gb = expectedGameBank[playerPath];
        if (!gb) {
            gb = expectedGameBank[playerPath] = {
                grants: 0,
                winnings: 0,
                betcost: 0,
                net: 0,
                bestGuessCount: 0,
                exactGuessCount: 0,
                winningBetCount: 0,
            };
        }
        assertBetNumber(ctx, gb.winnings);
        assertBetNumber(ctx, gb.grants);
        assertBetNumber(ctx, gb.betcost);
        assertBetNumber(ctx, gb.net, '');
        ctx.assertEqual(gb.winnings - gb.betcost + gb.grants, gb.net, '.net should be function of .winnings, .betcost, .grants', gb);
        ctx.assertEqual(typeof gb.bestGuessCount, 'number');
        ctx.assertEqual(typeof gb.exactGuessCount, 'number');
        ctx.assertEqual(typeof gb.winningBetCount, 'number');
        ctx.assertGE(gb.bestGuessCount, 0);
        ctx.assertGE(gb.exactGuessCount, 0);
        ctx.assertGE(gb.winningBetCount, 0);
        return gb;
    }
    verifyRoundNumbers(ctx.newCtx('roundNumber'), rounds);
    const orderedRounds = [];
    Object.values(rounds).forEach(r => { orderedRounds[r.roundNumber] = r; });
    for (let idx = 0; idx < orderedRounds.length; idx++) {
        const round = orderedRounds[idx];
        if (idx === 0 && !round) {
            ctx.log('Okay if [0] round is not defined ...');
        }
        else {
            ctx.assertDefined(round, 'expecting round at', idx, rounds);
            const roundPath = round.roundPath;
            try {
                // Updates per-player game-bank as rounds are verified
                verifyRound(ctx.newCtx(`Round.${round.roundNumber}`), game, getExpectedGameBank, roundPath, round);
            }
            catch (err) {
                throw ctx.chainError(err, 'In round', idx, roundPath);
            }
        }
    }
    // Verify game-level player winnings
    for (const [playerPath, gameState] of Object.entries(game.players)) {
        const expectedBank = getExpectedGameBank(playerPath);
        ctx.assertEqual(typeof gameState.winnings, 'number');
        // Game-level "winnings" is the net winnings (no winnings/betcost/grant
        // breakdown on the game)
        ctx.assertEqual(gameState.winnings, expectedBank.net, 'Game winnings amount for', playerPath);
        ctx.assertEqual(gameState.winningBetCount, expectedBank.winningBetCount, 'Game winning bet count for', playerPath);
        ctx.assertEqual(gameState.bestGuessCount, expectedBank.bestGuessCount, 'Game best guess count for', playerPath);
        ctx.assertEqual(gameState.exactGuessCount, expectedBank.exactGuessCount, 'Game exact winning guess count for', playerPath);
        ctx.assertOk(gameState.isActive, 'Game isActive must be true', gameState, 'for player', playerPath);
    }
    ctx.log('Game verified.');
    return Promise.resolve();
}
// Verify roundNumbers are unique, greater than 0
function verifyRoundNumbers(ctx, roundsSet) {
    const seenId = new Set();
    const rounds = Object.values(roundsSet);
    const maxValidId = rounds.length;
    rounds.forEach(r => {
        const id = r.roundNumber;
        ctx.assertFalse(seenId.has(id), 'Duplicate id', id, seenId.values());
        seenId.add(id);
        ctx.assertEqual(typeof id, 'number');
        ctx.assertGE(id, 0, 'id must non-negative');
        ctx.assertLE(id, maxValidId, 'id should be <= maxValidId');
        ctx.assertOk(Number.isInteger(id), `"${id}" must be a whole integer`);
    });
}
function verifyRound(ctx, game, getExpectedGameBankFor, roundPath, round) {
    var _a, _b;
    const betSpotCount = 7; // +1 for underfow
    const perRoundGrant = game.options.betGrantCount * game.options.betUnit;
    const betsPerRound = game.options.betsPerRound;
    const gamePlayers = Object.keys(game.players);
    ctx.assertDefined(round);
    ctx.assertNotNull(round.results, 'round must have results');
    ctx.assertNotNull(round.results.answer, 'round must have an answer');
    if (!(round.results.winningGuesses)) {
        ctx.log('no winning guess in', roundPath);
    }
    verifyUserList(ctx, gamePlayers, round.players, 'round players');
    // BetSpots are as expected:
    ctx.assertDefined(round.betSpots[Persistent.BetSpot_Underflow]);
    for (let i = 0; i < betSpotCount; i++) {
        const path = Persistent.makeBetSpotPath(i);
        ctx.assertHasProperty(round.betSpots, path, 'betSpot', i, path);
    }
    ctx.assertUndefined(round.betSpots[Persistent.makeBetSpotPath(betSpotCount)]);
    ctx.assertEqual(Object.keys(round.betSpots).length, betSpotCount + 1); // +1 for underflow
    // All players have input and bets, and a subset can have banks
    verifyUserList(ctx, gamePlayers, Object.keys(round.playerInput), 'playerInput list');
    verifyUserList(ctx, gamePlayers, Object.keys(round.playerBet), 'playerBet list');
    verifyUserList(ctx, gamePlayers, Object.keys(round.playerBank), 'playerBank list', 'LE');
    // All playerBet objects are consistent and complete
    for (const [playerPath, bet] of Object.entries(round.playerBet)) {
        ctx.assertOk(bet.readyToGuess, `playerBet/${playerPath}/readyToGuess must be true`);
        const usedPaths = Object.keys(bet.bets);
        usedPaths.forEach(betSpotPath => {
            ctx.assertDefined(round.betSpots[betSpotPath], 'betSpot at', betSpotPath);
            const val = bet.bets[betSpotPath];
            if (val) {
                ctx.assertEqual(typeof (val), 'number');
                ctx.assertGT(val, 0); // XXX zero is okay?
                ctx.assertEqual(val % game.options.betUnit, 0, 'bet', val, 'should be multiple of', game.options.betUnit);
            }
        });
    }
    // All playerBank entries are consistent and well structured.
    // More winnings/payout validation done below ...
    for (const [_, bank] of Object.entries(round.playerBank)) {
        ctx.assertEqual(bank.grant, perRoundGrant, 'bank.grant is as expected');
        ctx.assertGE(bank.winnings, 0, 'winnings must be positive');
        ctx.assertEqual(bank.winnings % game.options.betUnit, 0, 'winnings must be multiple of betUnit');
        ctx.assertGE(bank.betcost, 0, 'betcost');
        ctx.assertEqual(bank.betcost % game.options.betUnit, 0, 'betcost must be multiple of betUnit');
        ctx.assertEqual(bank.net, bank.winnings - bank.betcost + bank.grant, '.net consistent with .winnings, .betcost and .grant:', bank);
        for (const [betSpotPath, payout] of Object.entries(bank.betPayouts)) {
            const spot = round.betSpots[betSpotPath];
            ctx.assertDefined(spot);
            ctx.assertEqual(payout.multiplier, spot.payoutMultiplier);
            ctx.assertDefined(betSpotPath);
            ctx.assertGE(payout.bet, 0); // may get limited down to $0
            ctx.assertEqual(payout.bet % game.options.betUnit, 0, `${payout.bet} should be multiple of ${game.options.betUnit}`);
            if (payout.loss) {
                ctx.assertEqual(payout.loss, payout.bet);
                ctx.assertEqual(payout.payout, 0);
            }
            else {
                ctx.assertEqual(payout.payout, payout.bet * spot.payoutMultiplier);
            }
        }
    }
    // Confirm the guess layout is correct
    // guesses are in the right order
    // guesses map to the right player
    // guesses are laid out correctly
    // - same gap on either side
    // - middle skipped if even number of unique guesses)
    const layout = round.guessLayout;
    ctx.assertNotNull(layout);
    let undefLeft = 0;
    let undefRight = 0;
    for (let i = 0; i < betSpotCount; i++) {
        const spot = Persistent.makeBetSpotPath(i);
        ctx.assertDefined(round.betSpots[spot], 'betSpot', i);
        if (layout[spot]) {
            break;
        }
        undefLeft++;
    }
    for (let i = betSpotCount - 1; i >= 0; i--) {
        const spot = Persistent.makeBetSpotPath(i);
        ctx.assertDefined(round.betSpots[spot]);
        if (layout[spot]) {
            break;
        }
        undefRight++;
    }
    ctx.log('Undef:', undefLeft, undefRight);
    ctx.assertEqual(undefLeft, undefRight);
    let undefCount = 0;
    let defCount = 0;
    for (let i = 0; i < betSpotCount; i++) {
        const spot = Persistent.makeBetSpotPath(i);
        ctx.assertDefined(round.betSpots[spot], 'betSpot', i);
        if (layout[spot]) {
            defCount++;
        }
        else {
            undefCount++;
        }
    }
    ctx.log('Counts: undef:', undefCount, 'def:', defCount);
    const midSpot = Persistent.makeBetSpotPath(Math.floor(betSpotCount / 2));
    if ((defCount % 2) === 0) {
        // even set of guesses, middle is blank
        ctx.assertUndefined(layout[midSpot], 'Middle spot blank with', defCount, 'defined spots.');
    }
    else {
        ctx.assertDefined(layout[midSpot], 'Middle spot set with', defCount, 'defined spots');
    }
    ctx.assertUndefined(layout[Persistent.BetSpot_Underflow], 'No guesses should be in the underflow spot'); // not used in tests/UI yet
    const guessesByPlayer = {};
    for (const betSpotPath of Object.keys(layout)) {
        ctx.assertDefined(round.betSpots[betSpotPath], 'layout has unexpected bet spot', betSpotPath);
        ctx.assertNotEqual(betSpotPath, Persistent.BetSpot_Underflow, 'Guesses not allowed in underflow');
        const guesses = layout[betSpotPath] || {};
        let firstGuess;
        for (const [plyr, guess] of Object.entries(guesses)) { // all guesses on current betSpot
            if (firstGuess !== undefined) {
                ctx.assertEqual(firstGuess, guess.guessValue, 'All guesses on same spot should have same value.');
            }
            else {
                firstGuess = guess.guessValue;
            }
            ctx.assertUndefined(guessesByPlayer[plyr], 'Each player should only have one guess', JSON.stringify(guessesByPlayer), 'plus', betSpotPath, JSON.stringify(guess));
            guessesByPlayer[plyr] = {
                ...guess,
                betSpotPath,
            };
            ctx.assertEqual((_a = round.playerInput[plyr]) === null || _a === void 0 ? void 0 : _a.guess, guess.rawGuess, 'In', roundPath, 'guess for', plyr, 'is', guess, ', but playerInput is:', round.playerInput[plyr]);
        }
    }
    // We know (from above) that every player has a PlayerInput.  So make sure all
    // PlayerInput show up in the guessesByPlayer with the correct guess
    for (const [playerPath, input] of Object.entries(round.playerInput)) {
        ctx.assertDefined(input.guess);
        const guessObj = guessesByPlayer[playerPath];
        ctx.assertDefined(guessObj);
        ctx.assertEqual(input.guess, guessObj.rawGuess);
    }
    // Make sure guesses are laid out in the correct order
    const { bestGuess, bestSpot } = getBestSpotAndGuess(ctx.newCtx('findbest'), layout, round.betSpots, betSpotCount, round.results.answer);
    // Compute per-player limits on betting
    const limits = {};
    for (const playerPath of round.players) {
        const playerGameBank = getExpectedGameBankFor(playerPath);
        const bettingLimit = playerGameBank.net + perRoundGrant;
        ctx.log('Player', playerPath, 'betting limit', `($${perRoundGrant} + $${playerGameBank.net}) = $${bettingLimit}`);
        limits[playerPath] = {
            bettingLimit,
            totalBetValue: 0,
            betSpotCount: 0,
        };
    }
    // map from playerPath
    const expectedResults = {};
    // Initialize expectedWinnings for every player with the grant as the baseline
    for (const playerPath of round.players) {
        expectedResults[playerPath] = {
            grant: perRoundGrant,
            betcost: 0,
            fromAnswer: 0,
            winningBet: 0,
            losingBets: 0,
            winningBetCount: 0,
        };
    }
    // For the winning spot, gather best-guess information (bets handled later)
    ctx.log('best guess spot:', bestSpot);
    if (bestSpot === Persistent.BetSpot_Underflow) {
        ctx.assertEqual(round.results.winningGuesses, null);
    }
    else {
        const recordedWinners = round.results.winningGuesses;
        // ctx.deepEqual(round.results.winningGuesses, layout[bestSpot]);
        ctx.assertNotNull(recordedWinners, 'Round results should have some winning guesses:', round.results);
        ctx.assertDefined(bestGuess);
        const answerDelta = bestGuess - round.results.answer;
        const winners = layout[bestSpot];
        const bestAnswerPayout = game.options.bestAnswerGrantCount * game.options.betUnit;
        ctx.assertNotNull(winners);
        ctx.assertDefined(winners, 'Expecting at least one winner at', bestSpot);
        ctx.assertEqual(Object.keys(recordedWinners).length, Object.keys(winners).length, 'Round results:', round.results, 'Expected "winningGuesses":', winners);
        for (const playerPath of Object.keys(winners)) {
            ctx.assertDefined(expectedResults[playerPath]);
            ctx.assertEqual(expectedResults[playerPath].fromAnswer, 0);
            expectedResults[playerPath].fromAnswer = bestAnswerPayout;
            const playerGameBank = getExpectedGameBankFor(playerPath);
            playerGameBank.bestGuessCount++;
            if (answerDelta === 0) {
                playerGameBank.exactGuessCount++;
            }
        }
    }
    // Check all playerInput is consistent with winners list
    for (const [playerPath, input] of Object.entries(round.playerInput)) {
        ctx.assertDefined(input.guess);
        if (parseGuess(ctx, input.guess) === bestGuess) {
            ctx.assertHasProperty(expectedResults, playerPath);
        }
    }
    // Sort betSpots in a stable order (mimicking how Round.ts computes this order).
    // Should probably be a more sensible order (lower to higher value bets)?
    const betSpotPaths = Object.keys(round.betSpots).sort();
    const winningSpotPaths = [
        bestSpot,
    ];
    // Check every player's winning and losing bets
    for (const [playerPathStr, playerBets] of Object.entries(round.playerBet)) {
        const playerPath = Persistent.asPlayerPath(playerPathStr);
        ctx.assertTrue(playerBets.readyToGuess);
        const playerLimit = limits[playerPath];
        ctx.assertDefined(playerLimit, 'Player', playerPath, 'needs a limit');
        const playerExpectedResults = expectedResults[playerPath];
        ctx.assertDefined(playerExpectedResults);
        for (const betSpotPath of betSpotPaths) {
            const betValue = playerBets.bets[betSpotPath];
            // First tally up all the losing bet spots
            if (betValue && !winningSpotPaths.includes(betSpotPath)) {
                // Shape of betValue verified above
                ctx.assertLE(playerLimit.totalBetValue, playerLimit.bettingLimit);
                const betcap = playerLimit.bettingLimit - playerLimit.totalBetValue;
                const payout = getBetSpotPayout(ctx, round, playerPath, betSpotPath);
                ctx.assertEqual(typeof payout.loss, 'number');
                ctx.assertEqual(typeof payout.bet, 'number');
                ctx.assertEqual(typeof payout.multiplier, 'number');
                ctx.assertEqual(payout.payout, 0, 'payout on losing bet should be 0');
                // betValue is the pre-limits bet amount.  payout.bet is the official
                // recorded post-limits bet amount
                let allowedBet = betValue;
                if (betcap < betValue) {
                    ctx.log('Player', playerPath, 'overlimit on bet at (losing)', betSpotPath, `limited $${betValue} to $${betcap}`);
                    allowedBet = betcap;
                }
                if (playerLimit.betSpotCount >= betsPerRound) {
                    ctx.log('Player', playerPath, 'bet on too many spots.  Ignoring losing bet at', betSpotPath);
                    allowedBet = 0;
                }
                ctx.assertEqual(allowedBet, payout.bet, 'Player', playerPath, 'allowedBet should match (losing) actual bet on', betSpotPath);
                if (allowedBet > 0) {
                    playerLimit.totalBetValue += allowedBet;
                    playerLimit.betSpotCount += 1;
                    ctx.assertEqual(allowedBet, payout.loss, 'Player', playerPath, 'allowedBet should match actual loss on', betSpotPath);
                    playerExpectedResults.betcost += allowedBet;
                    playerExpectedResults.losingBets += allowedBet;
                }
            }
        }
        // XXX only one winning spot for now
        ctx.assertEqual(winningSpotPaths.length, 1);
        const val = playerBets.bets[bestSpot]; // Val is the raw (pre-limits) bet amount
        if (val) {
            ctx.log('Player', playerPath, 'bet', val, 'on', bestSpot);
            const payout = getBetSpotPayout(ctx, round, playerPath, bestSpot);
            ctx.assertEqual(payout.loss, 0, 'no loss on winning bet', payout);
            ctx.assertEqual(typeof payout.bet, 'number', 'with payout', payout);
            ctx.assertEqual(typeof payout.multiplier, 'number', 'with payout', payout);
            ctx.assertEqual(typeof payout.payout, 'number', 'with payout', payout);
            ctx.assertLE(payout.bet, val, // payout.bet is a post-limits actual allowed bet value
            `Winning payout $${val} should be no more than than recorded .bet:`, payout);
            ctx.assertEqual(payout.multiplier, (_b = round.betSpots[bestSpot]) === null || _b === void 0 ? void 0 : _b.payoutMultiplier, 'with payout', payout);
            // Shape of val verified above
            ctx.assertLE(playerLimit.totalBetValue, playerLimit.bettingLimit);
            const betcap = playerLimit.bettingLimit - playerLimit.totalBetValue;
            let allowedBet = val;
            if (betcap < allowedBet) {
                ctx.log('Player', playerPath, 'overlimit on bet at winning', bestSpot, `limited $${allowedBet} to $${betcap}`);
                allowedBet = betcap;
            }
            if (playerLimit.betSpotCount >= betsPerRound) {
                ctx.log('Player', playerPath, 'bet on too many spots.  Ignoring winning bet at', bestSpot);
                allowedBet = 0;
            }
            ctx.assertEqual(payout.bet, allowedBet, 'Player', playerPath, 'post-limit winning bet on', bestSpot, 'should match computed bet');
            playerLimit.totalBetValue += allowedBet;
            if (allowedBet > 0) {
                playerLimit.betSpotCount += 1;
            }
            const betPayout = payout.multiplier * allowedBet;
            ctx.assertEqual(betPayout, payout.payout, 'bet payout amount for', playerPath, 'on', bestSpot);
            playerExpectedResults.betcost += allowedBet;
            playerExpectedResults.winningBet += betPayout;
            if (allowedBet > 0) {
                playerExpectedResults.winningBetCount += 1;
            }
        }
        ctx.log('Limits for', playerPath, playerLimit);
        ctx.log('expectedResults for', playerPath, playerExpectedResults);
    }
    // Check expected winnings match actual bank
    const bankPlayers = Object.keys(round.playerBank);
    const computedWinners = Object.keys(expectedResults);
    ctx.assertGE(bankPlayers.length, computedWinners.length, 'Players in round bank', round.playerBank, 'at least contains winnner', computedWinners);
    bankPlayers.forEach(player => {
        const bank = round.playerBank[player];
        ctx.assertDefined(bank, player, 'must have bank');
        const expected = expectedResults[player];
        ctx.assertDefined(expected, player, 'must have expected winnings');
        const expectedNet = expected.fromAnswer + expected.winningBet - expected.betcost + expected.grant;
        ctx.log('Player', player, `expected to bank $${expectedNet} from`, expected, `and $${perRoundGrant} grant.  Recorded bank:`, bank);
        ctx.assertEqual(bank.net, expectedNet, 'Net result of bets & guess reward for', player);
        ctx.assertEqual(bank.betcost, expected.betcost, 'Cost of bets for', player);
        ctx.assertEqual(bank.winnings, expected.winningBet + expected.fromAnswer, 'Raw bet+answer winnings for', player);
        if (bank.winningGuess !== undefined) {
            ctx.assertEqual(bank.winningGuessPayout, expected.fromAnswer);
        }
        else {
            ctx.assertEqual(bank.winningGuessPayout, 0);
            ctx.assertEqual(expected.fromAnswer, 0);
        }
        const playerGameBank = getExpectedGameBankFor(player);
        playerGameBank.winnings += bank.winnings;
        playerGameBank.betcost += bank.betcost;
        playerGameBank.grants += bank.grant;
        playerGameBank.net += bank.net;
        playerGameBank.winningBetCount += expected.winningBetCount;
    });
    ctx.log('Verified round', roundPath);
}
function getBestSpotAndGuess(ctx, layout, betSpots, betSpotCount, answer) {
    let prevGuess;
    let bestSpot = Persistent.BetSpot_Underflow;
    let bestGuess;
    for (let i = 0; i < betSpotCount; i++) {
        const spot = Persistent.makeBetSpotPath(i);
        ctx.assertHasProperty(betSpots, spot, 'betSpot', i, spot);
        const spotGuesses = layout[spot];
        if (spotGuesses !== undefined) {
            const guesses = Object.values(spotGuesses);
            // Make sure guesses are in right order and consistent
            const firstGuess = guesses[0];
            ctx.assertDefined(firstGuess);
            const firstVal = parseGuess(ctx, firstGuess.rawGuess);
            if (prevGuess !== undefined) {
                ctx.assertGT(firstVal, prevGuess, 'Next spot guess must be greater than previous:', spot, 'in', layout);
            }
            prevGuess = firstVal;
            guesses.forEach(guess => {
                ctx.assertEqual(firstVal, parseGuess(ctx, guess.rawGuess), 'All guesses on spot', spot, 'should have same value', guess.rawGuess, 'in', layout);
            });
            // Track best guess so far
            if (firstVal <= answer) {
                bestSpot = spot;
                bestGuess = firstVal;
            }
        }
    }
    return { bestSpot, bestGuess };
}
function getBetSpotPayout(ctx, round, playerPath, spot) {
    const bank = round.playerBank[playerPath];
    ctx.assertDefined(bank, 'Expecting', playerPath, 'to have some bank in:', round.playerBank);
    const betPayout = bank.betPayouts[spot];
    ctx.assertDefined(betPayout, `No payout for ${playerPath} on ${spot}:`, round.roundNumber, round.playerBank);
    return betPayout;
}
function verifyUserList(ctx, gameList, roundList, label, op = 'EQ') {
    const gameSet = new Set(gameList);
    const roundSet = new Set(roundList);
    ctx.assertEqual(gameSet.size, gameList.length, `${label}: game list has duplicates`);
    ctx.assertEqual(roundSet.size, roundList.length, `${label}: round list has duplicates`);
    if (op === 'EQ') {
        ctx.assertEqual(gameList.length, roundList.length, `${label}: game and round should have same size`);
        for (const gameElem of gameSet) {
            ctx.assertOk(roundSet.has(gameElem), `${label}: round missing entry ${gameElem}`);
        }
    }
    else if (op === 'LE') {
        ctx.assertLE(roundList.length, gameList.length, `${label}: round should be a subset of game`);
        for (const roundElem of roundSet) {
            ctx.assertOk(gameSet.has(roundElem), `${label}: round has extra entry ${roundElem}`);
        }
    }
}
function fetchAll(ctx, coll) {
    const result = {};
    return coll.getDocs().then(query => {
        query.forEach(doc => {
            const blob = doc.data();
            if (blob) {
                result[doc.id()] = blob;
            }
            else {
                throw ctx.newError(`No blob for ${doc.id()}`);
            }
        });
        return result;
    });
}
function parseGuess(ctx, rawGuess) {
    const n = Number(rawGuess);
    if (isNaN(n)) {
        throw ctx.newError('Invalid guess', rawGuess);
    }
    return n;
}
function verify(ctx, db, gamePath) {
    ctx.log('Verifying game', gamePath);
    const gameRef = Game_1.Game.gameDoc(ctx, db, gamePath);
    const roundColl = Game_1.Game.roundsCollection(ctx, gameRef);
    const roundsProm = roundColl.getDocs().then(query => {
        const rounds = {};
        const subColls = [];
        query.forEach(doc => {
            const round = doc.data();
            if (!round) {
                throw ctx.newError(`No round for ${doc.id()}`);
            }
            const playerBankP = fetchAll(ctx, Round_1.Round.getPlayerBankCollection(ctx, doc.ref()));
            const playerInputP = fetchAll(ctx, Round_1.Round.getPlayerInputCollection(ctx, doc.ref()));
            const playerBetP = fetchAll(ctx, Round_1.Round.getPlayerBetCollection(ctx, doc.ref()));
            const guessLayoutP = Round_1.Round.getRoundLayoutDocRef(ctx, doc.ref()).get();
            const roundResultsP = Round_1.Round.getRoundResultsDocRef(ctx, doc.ref()).get();
            subColls.push(Promise.all([playerBankP, playerBetP, playerInputP, guessLayoutP, roundResultsP]).then(([playerBank, playerBet, playerInput, guessLayout, results]) => {
                const path = doc.id();
                rounds[path] = {
                    ...round,
                    roundPath: path,
                    playerBank,
                    playerBet,
                    playerInput,
                    guessLayout: (guessLayout === null || guessLayout === void 0 ? void 0 : guessLayout.data()) || null,
                    results: (results === null || results === void 0 ? void 0 : results.data()) || null,
                };
            }));
        });
        return Promise.all(subColls).then(() => {
            return rounds;
        });
    });
    return gameRef.get().then(gameDoc => {
        ctx.log('Loaded data');
        const game = gameDoc.data();
        ctx.assertDefined(game, 'must have a game for', gameDoc.id());
        return roundsProm.then(rounds => {
            return verifyGame(ctx, game, rounds);
        });
    });
}
//# sourceMappingURL=Verifier.js.map